import { api, endpoints } from '@/api'
import { PaymentMethodType } from '@/store/useAddMoneyStore'
import { currencyUtils } from '@/utils'
import {
  CryptoAddressDTO,
  CryptoAddressPayload,
  CurrencyInBlockchainDTO,
  CurrencyInBlockchainPayload,
  WalletPayload,
  CreateKemCardDTO,
  GetKemCardParams,
  KemCardActionsPayload,
  KemCardDTO,
  VerifyCardIssueStatusDTO,
  CryptoAddressWithdrawDTO,
  CreateCryptoAddressWithdrawPayload,
  DeleteCryptoAddressWithdrawPayload,
  CryptoAddressWithdrawParams,
  CryptoAddressWithdrawList,
  GetKemCardEncryptedParams,
  GetInterlaceWidgetTokenParams,
  GetInterlaceWidgetTokenResponse,
  KemCardEncryptedDTO,
  GetCardPinDTO,
  SetCardPinPayload,
  GetUnigoxOffRampPaymentRailsParams,
  GetUnigoxOffRampPaymentRailsDTO,
  GetUnigoxOffRampInstitutionsParams,
  GetUnigoxOffRampInstitutionsDTO,
  CreateUnigoxOffRampPaymentDetailsPayload,
  UnigoxOffRampPaymentDetailsDTO,
  DeleteUnigoxOffRampPaymentDetailsParams,
} from './walletTypes'
import { currencyApi } from '../currencies/currencyApi'
import { CurrencyCatalogsDTO } from '../currencies/currencyTypes'

// Raw API response types (matching React Native structure)
interface RawBalance {
  currency: string
  percent_change_24h: string
  fiat_currency: string
  amount: string
  currency_type: 'FIAT' | 'CRYPTO'
  revenue_24h_fiat_amount: string
  fiat_amount: string
  price_for_unit: number
}

interface RawPocket {
  id: string
  is_main: boolean
  balance: RawBalance
  accounts: any[]
}

interface RawCard {
  id: string
  first_six: string
  last_four: string
  state: string
  network: string
  card_type: string
}

interface RawWalletDTO {
  id: string
  entity_type: 'private' | 'business'
  state: 'active' | 'inactive' | 'suspended'
  name?: string
  img_url?: string
  is_main: boolean
  display_fiat_currency: string
  pockets: RawPocket[]
  crypto_addresses: any[]
  total_amount: string // This is a string in the API response!
  cards: RawCard[]
}

export interface WalletCurrency {
  id: string
  name: string
  code: string
  amount: number
  amountWithoutNumberConversion: string
  icon: string
  label: string
  fiatAmount: number
  fiatCurrency: string
  percentageChange24h?: number
  revenue24hFiatAmount?: number
  pricePerUnit: number
  ceilingValueForValidation: number
  availableOnRampPaymentMethods: PaymentMethodType[]
  // Adapted fields for display
  formattedFiatBalance: string
  formattedAmountBalance: string
  formattedUSDBalance: string
  formattedStablecoinBalance: string
  usdBalance: number
  displayCurrencyCode: string
  symbol: string
  avatarBGRefColor: string | [string, string, ...string[]]
  // OnRamp limits
  minForOnRamp?: {
    fiat_currency: string
    fiat_amount: string
    formattedAmount: string
  }
  maxForOnRamp?: {
    fiat_currency: string
    fiat_amount: string
    formattedAmount: string
  }
}

export interface WalletDTO {
  walletId: string
  displayCurrency: string
  totalBalance: number
  completeCurrencies: WalletCurrency[]
  cards: Array<{
    id: string
    first_six: string
    last_four: string
    state: string
    network: string
    card_type: string
  }>
}

// Transform raw API response to WalletDTO with currency catalog data
function transformRawWalletData(
  rawWallet: RawWalletDTO,
  currenciesCatalog?: CurrencyCatalogsDTO[],
  displayCurrency?: string,
): WalletDTO {
  // Create a map for quick currency lookup by ISIN
  const currencyCatalogMap = new Map<string, CurrencyCatalogsDTO>()
  if (currenciesCatalog) {
    currenciesCatalog.forEach((currency) => {
      currencyCatalogMap.set(currency.isin, currency)
    })
  }

  const completeCurrencies: WalletCurrency[] = rawWallet.pockets.map(
    (pocket) => {
      const currencyCatalogInfo = currencyCatalogMap.get(
        pocket.balance.currency,
      )

      // Fallback values if catalog data is not available
      const fallbackName = pocket.balance.currency

      // Fallback icons and colors based on currency code
      const getCurrencyFallbacks = (currencyCode: string) => {
        switch (currencyCode.toUpperCase()) {
          case 'USDT':
            return {
              icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
              bgColor: '#33CC67',
              label: '$',
              symbol: '$',
            }
          case 'BTC':
            return {
              icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/btc.png',
              bgColor: '#F7931A',
              label: '₿',
              symbol: '₿',
            }
          case 'ETH':
            return {
              icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/eth.png',
              bgColor: '#7059FF',
              label: 'Ξ',
              symbol: 'Ξ',
            }
          case 'XAUT':
            return {
              icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/gold.png',
              bgColor: ['#FCFBE7', '#FFD475', '#DD8536'],
              label: 'Au',
              symbol: 'Au',
            }
          case 'SOL':
            return {
              icon: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
              bgColor: '#000000',
              label: 'SOL',
              symbol: 'SOL',
            }
          default:
            return {
              icon: '',
              bgColor: '#6B7280',
              label: currencyCode,
              symbol: '$',
            }
        }
      }

      const fallbacks = getCurrencyFallbacks(pocket.balance.currency)
      const fallbackIcon = fallbacks.icon
      const fallbackLabel = fallbacks.label
      const fallbackSymbol = fallbacks.symbol
      const fallbackBGColor = fallbacks.bgColor

      // Temporary stub for availableOnRampPaymentMethods
      // TODO: Fetch from API via useGetOnRampPriceLimits
      const getAvailablePaymentMethods = (
        currencyCode: string,
      ): PaymentMethodType[] => {
        const upperCode = currencyCode.toUpperCase()
        // Set available payment methods for major currencies
        switch (upperCode) {
          case 'USDT':
          case 'BTC':
          case 'ETH':
            return ['card', 'apple-pay', 'google-pay', 'bank-transfer']
          case 'SOL':
          case 'XAUT':
            return ['card', 'apple-pay', 'bank-transfer']
          default:
            return ['card', 'bank-transfer']
        }
      }

      const transformedCurrency = {
        id: pocket.id,
        name: currencyCatalogInfo?.name || fallbackName,
        code: pocket.balance.currency,
        amount: Number(pocket.balance.amount) || 0,
        amountWithoutNumberConversion: pocket.balance.amount,
        icon: currencyCatalogInfo?.img_url || fallbackIcon,
        label: currencyCatalogInfo?.label || fallbackLabel,
        fiatAmount: Number(pocket.balance.fiat_amount) || 0,
        fiatCurrency: pocket.balance.fiat_currency,
        percentageChange24h: Number(pocket.balance.percent_change_24h) || 0,
        revenue24hFiatAmount:
          Number(pocket.balance.revenue_24h_fiat_amount) || 0,
        pricePerUnit: pocket.balance.price_for_unit,
        ceilingValueForValidation: 0, // TODO: Calculate like in React Native
        availableOnRampPaymentMethods: getAvailablePaymentMethods(
          pocket.balance.currency,
        ),
        // Display formatting with proper currency handling
        formattedFiatBalance: currencyUtils.formatDynamicCurrency({
          amount: Number(pocket.balance.fiat_amount || 0),
          currencyCode:
            displayCurrency || rawWallet.display_fiat_currency || 'USD',
          showDecimals: true,
        }),
        formattedAmountBalance: currencyUtils.formatDynamicCurrency({
          amount: Number(pocket.balance.amount || 0),
          currencyCode: pocket.balance.currency,
          showDecimals: true,
        }),
        formattedUSDBalance: currencyUtils.formatDynamicCurrency({
          amount: Number(pocket.balance.fiat_amount || 0),
          currencyCode: 'USD',
          showDecimals: true,
        }),
        formattedStablecoinBalance: currencyUtils.formatDynamicCurrency({
          amount: Number(pocket.balance.amount || 0),
          currencyCode: pocket.balance.currency,
          showDecimals: true,
        }),
        usdBalance: Number(pocket.balance.fiat_amount) || 0,
        displayCurrencyCode: pocket.balance.currency,
        symbol: (currencyCatalogInfo as any)?.symbol || fallbackSymbol,
        avatarBGRefColor:
          (currencyCatalogInfo as any)?.reference_color || fallbackBGColor,
      }

      // Special debug for ETH
      if (pocket.balance.currency === 'ETH') {
        // ETH specific handling can be added here
      }

      return transformedCurrency
    },
  )

  const result = {
    walletId: rawWallet.id,
    displayCurrency: rawWallet.display_fiat_currency,
    totalBalance: Number(rawWallet.total_amount) || 0,
    completeCurrencies,
    cards: rawWallet.cards || [],
  }

  return result
}

export async function fetchWallet(): Promise<WalletDTO> {
  // Fetch wallet data and currencies catalog in parallel
  const [walletRes, currenciesRes] = await Promise.allSettled([
    api.get<RawWalletDTO>(endpoints.wallet.getWallet),
    currencyApi.fetchCurrencies({ is_available: true }),
  ])

  // Handle wallet data
  if (walletRes.status === 'rejected') {
    throw walletRes.reason
  }

  // Handle currencies catalog (optional)
  let currenciesCatalog: CurrencyCatalogsDTO[] | undefined
  if (currenciesRes.status === 'fulfilled') {
    currenciesCatalog = currenciesRes.value.currencies
  }

  const transformedData = transformRawWalletData(
    walletRes.value.data,
    currenciesCatalog,
    walletRes.value.data.display_fiat_currency,
  )

  return transformedData
}

export async function updateWallet(payload: WalletPayload): Promise<any> {
  const res = await api.post(endpoints.wallet.updateWallet, payload)
  return res.data
}

// Crypto address API functions
export async function fetchOrCreateCryptoAddress(
  payload: CryptoAddressPayload,
): Promise<CryptoAddressDTO> {
  const res = await api.post<CryptoAddressDTO>(
    endpoints.wallet.cryptoAddress,
    payload,
  )
  return res.data
}

// Withdraw crypto address API functions
export async function fetchWithdrawCryptoAddressByNetworkCode({
  networkCode,
}: CryptoAddressWithdrawParams): Promise<CryptoAddressWithdrawList> {
  const res = await api.get<CryptoAddressWithdrawList>(
    endpoints.wallet.withdrawCryptoAddressByNetwork(networkCode),
  )
  return res.data
}

export async function createWithdrawCryptoAddress(
  payload: CreateCryptoAddressWithdrawPayload,
): Promise<CryptoAddressWithdrawDTO> {
  const { grantToken, ...requestPayload } = payload

  const headers = grantToken ? { 'x-grant-token': grantToken } : undefined

  const res = await api.post<CryptoAddressWithdrawDTO>(
    endpoints.wallet.withdrawCryptoAddress,
    requestPayload,
    { headers },
  )
  return res.data
}

export async function deleteWithdrawCryptoAddress(
  payload: DeleteCryptoAddressWithdrawPayload,
): Promise<void> {
  const res = await api.delete(endpoints.wallet.withdrawCryptoAddress, {
    data: payload,
  })
  return res.data
}

export async function fetchCurrencyInBlockchain(
  payload: CurrencyInBlockchainPayload,
): Promise<CurrencyInBlockchainDTO> {
  const res = await api.get<CurrencyInBlockchainDTO>(
    endpoints.wallet.aboutCurrencyInBlockchain(
      payload.currency_isin,
      payload.blockchain_code,
    ),
  )
  return res.data
}

// Kem Card API functions
export async function createKemCard(): Promise<CreateKemCardDTO> {
  const res = await api.post<CreateKemCardDTO>(endpoints.wallet.createKemCard)
  return res.data
}

/**
 * @deprecated Use getKemCardEncrypted for secure card data retrieval
 * This function returns plaintext card data and should only be used for backward compatibility
 */
export async function getKemCard({
  cardId,
}: GetKemCardParams): Promise<KemCardDTO> {
  const res = await api.get<KemCardDTO>(endpoints.wallet.getKemCard(cardId))
  return res.data
}

/**
 * Fetches encrypted card data from RAIN provider
 * Card number and CVV are encrypted with AES-GCM
 *
 * @param params - card_id and session_id for the encrypted endpoint
 * @returns Encrypted card data that needs to be decrypted client-side
 */
export async function getKemCardEncrypted(
  params: GetKemCardEncryptedParams,
): Promise<KemCardEncryptedDTO> {
  const res = await api.get<KemCardEncryptedDTO>(
    endpoints.wallet.getKemCardEncrypted(params.card_id),
    {
      params: {
        session_id: params.session_id,
      },
    },
  )
  return res.data
}

export async function blockKemCard(
  payload: KemCardActionsPayload,
): Promise<void> {
  const res = await api.post(endpoints.wallet.blockKemCard, payload)
  return res.data
}

export async function unblockKemCard(
  payload: KemCardActionsPayload,
): Promise<void> {
  const res = await api.post(endpoints.wallet.unblockKemCard, payload)
  return res.data
}

export async function getCardPin(cardId: string, sessionId: string): Promise<GetCardPinDTO> {
  const response = await api.get(endpoints.wallet.getCardPin(cardId), { params: { session_id: sessionId } })
  return response.data
}

export async function setCardPin(payload: SetCardPinPayload): Promise<void> {
  const { grantToken, ...rest } = payload
  await api.post(endpoints.wallet.setCardPin, rest, {
    headers: { 'x-mfa-token': grantToken },
  })
}

export async function verifyCardIssueStatus(): Promise<VerifyCardIssueStatusDTO> {
  const res = await api.get<VerifyCardIssueStatusDTO>(
    endpoints.identity.verifyCardIssueStatus,
  )
  return res.data
}

// Unigox off-ramp API functions

export async function getUnigoxOffRampPaymentRails(
  params: GetUnigoxOffRampPaymentRailsParams,
): Promise<GetUnigoxOffRampPaymentRailsDTO> {
  const response = await api.get<GetUnigoxOffRampPaymentRailsDTO>(
    endpoints.wallet.getUnigoxOffRampPaymentRails,
    { params },
  )
  return response.data
}

export async function getUnigoxOffRampInstitutions(
  params: GetUnigoxOffRampInstitutionsParams,
): Promise<GetUnigoxOffRampInstitutionsDTO> {
  const response = await api.get<GetUnigoxOffRampInstitutionsDTO>(
    endpoints.wallet.getUnigoxOffRampInstitutions,
    { params },
  )
  return response.data
}

export async function getUnigoxOffRampPaymentDetails(): Promise<UnigoxOffRampPaymentDetailsDTO[]> {
  const response = await api.get<UnigoxOffRampPaymentDetailsDTO[]>(
    endpoints.wallet.unigoxOffRampPaymentDetails,
  )
  return response.data
}

export async function createUnigoxOffRampPaymentDetails(
  payload: CreateUnigoxOffRampPaymentDetailsPayload,
): Promise<UnigoxOffRampPaymentDetailsDTO> {
  const response = await api.post<UnigoxOffRampPaymentDetailsDTO>(
    endpoints.wallet.unigoxOffRampPaymentDetails,
    payload,
  )
  return response.data
}

export async function deleteUnigoxOffRampPaymentDetails(
  params: DeleteUnigoxOffRampPaymentDetailsParams,
): Promise<void> {
  const response = await api.delete(
    endpoints.wallet.deleteUnigoxOffRampPaymentDetails(params.payment_details_id),
  )
  return response.data
}

export async function deleteKemCard(
  payload: KemCardActionsPayload,
): Promise<void> {
  await api.delete(endpoints.wallet.deleteKemCard(payload.card_id))
}

export async function getInterlaceWidgetToken(
  params: GetInterlaceWidgetTokenParams,
): Promise<GetInterlaceWidgetTokenResponse> {
  const response = await api.get<GetInterlaceWidgetTokenResponse>(
    endpoints.wallet.getInterlaceWidgetToken(params.card_id),
    { params },
  )
  return response.data
}
